import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BetaPreviewNotice } from "@/components/BetaPreviewNotice";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { BallotWarning } from "@/components/BallotWarning";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { CopyButton } from "@/components/CopyButton";
import { usePlatformCopy } from "@/hooks/usePlatformCopy";
import { api, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ChevronLeft, ChevronRight, Printer, Download, CheckCircle2, AlertTriangle, PenLine, Keyboard, FileText,
} from "lucide-react";

const STEP_LABELS = ["Candidate", "Your information", "Declaration", "Signature", "Receipt"];

const buildReceiptHtml = (r) => `<!doctype html>
<html><head><meta charset="utf-8"><title>Witness Your Vote — Receipt ${r.declaration_number}</title>
<style>
body{font-family:Arial,Helvetica,sans-serif;color:#111827;max-width:640px;margin:40px auto;padding:0 20px;line-height:1.55}
.h{border-bottom:3px solid #14355e;padding-bottom:14px;margin-bottom:22px}
.mono{font-family:'Courier New',monospace;word-break:break-all}
.box{border:1px solid #d1d5db;border-radius:8px;padding:14px 16px;margin:14px 0;background:#f8fafc}
.small{font-size:12px;color:#4b5563}
.test{background:#fef3c7;border:1px solid #f59e0b;color:#92400e;padding:8px 12px;border-radius:6px;font-weight:bold;margin-bottom:16px}
</style></head><body>
<div class="h"><h1 style="margin:0;font-size:20px">Witness Your Vote Ontario — Declaration Receipt</h1>
<p class="small" style="margin:6px 0 0">Vote privately. Witness independently. Verify the count.</p></div>
${r.is_test ? '<div class="test">TEST DECLARATION — NOT A LIVE ELECTION RECORD</div>' : ""}
<p><strong>Declaration number:</strong> <span class="mono">${r.declaration_number}</span></p>
<p><strong>Declarant:</strong> ${r.voter_full_name}</p>
<p><strong>Candidate:</strong> ${r.candidate_name}<br>
<strong>Office:</strong> ${r.office_label}<br>
<strong>Municipality:</strong> ${r.municipality_name}<br>
<strong>Election:</strong> ${r.election_label}</p>
<p><strong>Received (UTC):</strong> ${r.submitted_at}<br>
<strong>Signature type:</strong> ${r.signature_type} · <strong>Declaration version:</strong> ${r.declaration_version}</p>
<div class="box"><strong>SHA-256 integrity hash</strong><br><span class="mono">${r.integrity_hash}</span></div>
${r.verify_url ? `<div class="box" style="display:flex;gap:16px;align-items:center">
<img src="${r.qr_url}" alt="QR code — scan to verify this receipt" width="120" height="120" style="border:1px solid #d1d5db;border-radius:6px"/>
<div><strong>Verify this receipt</strong><br><span class="small">Scan the QR code or visit:</span><br>
<span class="mono" style="font-size:12px">${r.verify_url}</span><br>
<span class="small">Verification shows record status only — it never reveals your identity, address, contact details or signature.</span></div>
</div>` : ""}
<p class="small">Keep this receipt. The integrity hash is a cryptographic fingerprint of your preserved declaration record.
You can verify it at any time using your declaration number and hash on the Verify a Declaration page.</p>
<p class="small">Witness Your Vote is an independent initiative. It is not an official voting system and does not replace the
official ballot, official election result, statutory recount or court process. Participation is voluntary.</p>
</body></html>`;

export default function Declare() {
  const { muniSlug, year, officeSlug, candSlug } = useParams();
  const copy = usePlatformCopy();
  const [candidate, setCandidate] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const [voter, setVoter] = useState({ full_name: "", qualifying_address: "", mailing_address: "", email: "", phone: "" });
  const [votedDate, setVotedDate] = useState("");
  const [voteMethod, setVoteMethod] = useState("online");
  const [confirms, setConfirms] = useState({});
  const [consents, setConsents] = useState({ verification: false, evidence_retention: false });
  const [sigTab, setSigTab] = useState("typed");
  const [typedSig, setTypedSig] = useState("");
  const [drawnSig, setDrawnSig] = useState(null);
  const [paperConfirm, setPaperConfirm] = useState(false);

  const statements = copy.declaration_statements || [];

  useEffect(() => {
    api
      .get(`/public/candidates/by-path/${muniSlug}/${year}/${officeSlug}/${candSlug}`)
      .then((r) => setCandidate(r.data))
      .catch(() => setError("Candidate not found or not currently participating."));
  }, [muniSlug, year, officeSlug, candSlug]);

  const electionOpen = candidate?.election?.status === "open";
  const allConfirmed = statements.length > 0 && statements.every((_, i) => confirms[`c${i + 1}`]);
  const voterValid =
    voter.full_name.trim().length >= 2 &&
    voter.qualifying_address.trim().length >= 5 &&
    /.+@.+\..+/.test(voter.email) &&
    votedDate;

  const signatureReady = useMemo(() => {
    if (sigTab === "typed") return typedSig.trim().length >= 2;
    if (sigTab === "drawn") return !!drawnSig;
    return paperConfirm;
  }, [sigTab, typedSig, drawnSig, paperConfirm]);

  const canSubmit = signatureReady && consents.verification && consents.evidence_retention;

  const submit = async () => {
    setSubmitting(true);
    try {
      const signature =
        sigTab === "typed"
          ? { type: "typed", data: typedSig.trim() }
          : sigTab === "drawn"
          ? { type: "drawn", data: drawnSig }
          : { type: "paper", data: null };
      const payload = {
        candidate_id: candidate.id,
        voter: {
          full_name: voter.full_name.trim(),
          qualifying_address: voter.qualifying_address.trim(),
          mailing_address: voter.mailing_address.trim() || null,
          email: voter.email.trim(),
          phone: voter.phone.trim() || null,
        },
        voted_date: votedDate,
        vote_method: voteMethod,
        consents,
        confirmations: Object.fromEntries(statements.map((_, i) => [`c${i + 1}`, !!confirms[`c${i + 1}`]])),
        signature,
        website: "",
      };
      const r = await api.post("/public/declarations", payload);
      setReceipt(r.data);
      setStep(5);
      window.scrollTo(0, 0);
    } catch (e) {
      const msg = e.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed — please review your entries.");
    } finally {
      setSubmitting(false);
    }
  };

  const printReceipt = () => {
    const w = window.open("", "_blank");
    w.document.write(buildReceiptHtml(receipt));
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const downloadReceipt = () => {
    const blob = new Blob([buildReceiptHtml(receipt)], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${receipt.declaration_number}-receipt.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPaperForm = () => {
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Witness Your Vote — Paper Declaration Form</title>
<style>body{font-family:Arial,sans-serif;max-width:680px;margin:40px auto;padding:0 20px;line-height:1.7;color:#111}
h1{font-size:18px;border-bottom:2px solid #14355e;padding-bottom:10px}.line{border-bottom:1px solid #999;min-height:26px;margin:4px 0 14px}
ol{font-size:12px}.small{font-size:11px;color:#444}</style></head><body>
<h1>Witness Your Vote Ontario — Paper Declaration Form</h1>
<p class="small">Candidate: <strong>${candidate.public_name}</strong> · ${candidate.office.label} · ${candidate.municipality.name} · ${candidate.election.label}</p>
<p>Full legal name:</p><div class="line"></div>
<p>Qualifying address:</p><div class="line"></div>
<p>Email:</p><div class="line"></div>
<p>Date voted / voting method:</p><div class="line"></div>
<p><strong>Declaration statements (initial each):</strong></p>
<ol>${statements.map((s) => `<li>${s} &nbsp;&nbsp;____</li>`).join("")}</ol>
<p>Signature:</p><div class="line"></div>
<p>Date:</p><div class="line"></div>
<p class="small">Never include your voting PIN, password, Voter Information Letter, or any ballot photograph. Participation is voluntary.
Witness Your Vote is an independent initiative and does not replace the official ballot or election result.</p>
</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  if (error)
    return (
      <div className="wyv-container py-20">
        <Card className="max-w-lg"><CardContent className="p-8">
          <h1 className="font-heading font-semibold text-xl mb-2">Not found</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button asChild variant="outline" className="mt-5"><Link to="/candidates">Back to directory</Link></Button>
        </CardContent></Card>
      </div>
    );

  if (!candidate)
    return <div className="wyv-container py-12"><Skeleton className="h-80 max-w-2xl mx-auto" /></div>;

  const initials = candidate.public_name.split(" ").map((w) => w[0]).slice(0, 2).join("");

  return (
    <div className="bg-background">
      <div className="wyv-container py-10 max-w-3xl">
        <BetaPreviewNotice className="mb-5" />
        {/* neutral WYV chrome */}
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <span className="font-heading font-semibold text-sm text-foreground">Voluntary Vote Declaration</span>
          {receipt?.is_test || copy.test_mode ? (
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 ml-auto">TEST MODE</Badge>
          ) : null}
        </div>

        {/* stepper */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs font-medium text-muted-foreground mb-2">
            <span data-testid="wizard-step-indicator">Step {step} of 5 — {STEP_LABELS[step - 1]}</span>
            <span className="hidden sm:block">{STEP_LABELS.join(" · ")}</span>
          </div>
          <Progress value={(step / 5) * 100} className="h-2" />
        </div>

        {/* persistent ballot secrecy warning — EVERY step */}
        <div className="mb-6">
          <BallotWarning testId="wizard-ballot-secrecy-warning" />
        </div>

        {!electionOpen && step < 5 && (
          <Card className="mb-6 border-slate-300 bg-slate-50" data-testid="wizard-election-closed-notice">
            <CardContent className="p-5 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">Declarations are not open for this election yet</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  This election is currently in “{candidate.election.status}” status. Declarations are only accepted while the
                  election is open. You may preview the steps below, but submission will be declined until declarations open.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.18 }}
          >
            {/* STEP 1: candidate confirmation */}
            {step === 1 && (
              <Card>
                <CardContent className="p-6 sm:p-8">
                  <h1 className="font-heading font-bold text-xl text-foreground mb-1">Confirm your candidate</h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    You are about to voluntarily declare that you voted for the candidate below. Please confirm this is
                    the correct candidate.
                  </p>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 flex items-center gap-4">
                    {candidate.photo ? (
                      <img src={assetUrl(candidate.photo)} alt={candidate.public_name} className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                    ) : (
                      <div className="h-16 w-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-xl font-heading font-bold text-slate-400">{initials}</div>
                    )}
                    <div>
                      <p className="font-heading font-semibold text-lg text-foreground" data-testid="wizard-candidate-name">{candidate.public_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {candidate.office.label} · {candidate.municipality.name} · {candidate.election.label}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    Not your candidate?{" "}
                    <Link to="/candidates" className="text-primary font-medium hover:underline">Find the right one in the directory.</Link>
                  </p>
                  <div className="flex justify-end mt-6">
                    <Button onClick={() => setStep(2)} data-testid="wizard-step1-confirm-button">
                      This is my candidate <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 2: voter info */}
            {step === 2 && (
              <Card>
                <CardContent className="p-6 sm:p-8">
                  <h1 className="font-heading font-bold text-xl text-foreground mb-1">Your information</h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    Your information stays private. It is never shown to candidates or the public, and is used only for
                    declaration verification and evidence preservation.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="full_name">Full legal name *</Label>
                      <Input id="full_name" className="mt-1.5" value={voter.full_name} onChange={(e) => setVoter({ ...voter, full_name: e.target.value })} data-testid="wizard-input-full-name" autoComplete="name" />
                    </div>
                    <div>
                      <Label htmlFor="qualifying_address">Qualifying address (where you are an eligible elector) *</Label>
                      <Input id="qualifying_address" className="mt-1.5" value={voter.qualifying_address} onChange={(e) => setVoter({ ...voter, qualifying_address: e.target.value })} data-testid="wizard-input-address" autoComplete="street-address" />
                    </div>
                    <div>
                      <Label htmlFor="mailing_address">Mailing address (if different)</Label>
                      <Input id="mailing_address" className="mt-1.5" value={voter.mailing_address} onChange={(e) => setVoter({ ...voter, mailing_address: e.target.value })} data-testid="wizard-input-mailing" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input id="email" type="email" className="mt-1.5" value={voter.email} onChange={(e) => setVoter({ ...voter, email: e.target.value })} data-testid="wizard-input-email" autoComplete="email" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone (optional)</Label>
                        <Input id="phone" className="mt-1.5" value={voter.phone} onChange={(e) => setVoter({ ...voter, phone: e.target.value })} data-testid="wizard-input-phone" autoComplete="tel" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="voted_date">Date you voted *</Label>
                        <Input id="voted_date" type="date" className="mt-1.5" value={votedDate} onChange={(e) => setVotedDate(e.target.value)} data-testid="wizard-input-voted-date" />
                      </div>
                      <div>
                        <Label>How you voted *</Label>
                        <RadioGroup value={voteMethod} onValueChange={setVoteMethod} className="mt-2.5 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="online" id="vm-online" data-testid="wizard-method-online" />
                            <Label htmlFor="vm-online" className="font-normal">Online (internet voting)</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="telephone" id="vm-phone" data-testid="wizard-method-telephone" />
                            <Label htmlFor="vm-phone" className="font-normal">Telephone</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="other_authorized" id="vm-other" data-testid="wizard-method-other" />
                            <Label htmlFor="vm-other" className="font-normal">Other authorized method</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3.5 mt-6">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">We will never ask for:</strong> voting PINs, passwords, Voter
                      Information Letters, ballot photos or screenshots, account access, Social Insurance Numbers, or
                      banking information.
                    </p>
                  </div>
                  <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-button">
                      <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button onClick={() => setStep(3)} disabled={!voterValid} data-testid="wizard-step2-next-button">
                      Continue <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 3: declaration statements */}
            {step === 3 && (
              <Card>
                <CardContent className="p-6 sm:p-8">
                  <h1 className="font-heading font-bold text-xl text-foreground mb-1">Your declaration</h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    Please read and confirm each statement. All statements must be confirmed to continue.
                  </p>
                  <div className="space-y-3">
                    {statements.map((s, i) => {
                      const key = `c${i + 1}`;
                      return (
                        <label key={key} className={`flex items-start gap-3 rounded-lg border p-3.5 cursor-pointer ${confirms[key] ? "border-primary/40 bg-blue-50/50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                          <Checkbox
                            checked={!!confirms[key]}
                            onCheckedChange={(v) => setConfirms((c) => ({ ...c, [key]: !!v }))}
                            className="mt-0.5"
                            data-testid={`wizard-statement-${i + 1}`}
                          />
                          <span className="text-sm text-foreground leading-relaxed">{s}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <button
                      type="button"
                      className="text-xs font-medium text-primary hover:underline"
                      onClick={() => setConfirms(Object.fromEntries(statements.map((_, i) => [`c${i + 1}`, true])))}
                      data-testid="wizard-confirm-all-button"
                    >
                      I have read all statements — confirm all
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {Object.values(confirms).filter(Boolean).length} of {statements.length} confirmed
                    </span>
                  </div>
                  <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={() => setStep(2)} data-testid="wizard-back-button">
                      <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button onClick={() => setStep(4)} disabled={!allConfirmed} data-testid="wizard-step3-next-button">
                      Continue <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 4: signature + consents */}
            {step === 4 && (
              <Card>
                <CardContent className="p-6 sm:p-8">
                  <h1 className="font-heading font-bold text-xl text-foreground mb-1">Sign your declaration</h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    Choose how to sign. This is an electronic declaration — it is not a commissioned affidavit.
                  </p>
                  <Tabs value={sigTab} onValueChange={setSigTab}>
                    <TabsList className="grid grid-cols-3 w-full">
                      <TabsTrigger value="typed" data-testid="wizard-sig-tab-typed"><Keyboard className="h-4 w-4 mr-1.5" /> Typed</TabsTrigger>
                      <TabsTrigger value="drawn" data-testid="wizard-sig-tab-drawn"><PenLine className="h-4 w-4 mr-1.5" /> Drawn</TabsTrigger>
                      <TabsTrigger value="paper" data-testid="wizard-sig-tab-paper"><FileText className="h-4 w-4 mr-1.5" /> Paper</TabsTrigger>
                    </TabsList>
                    <TabsContent value="typed" className="mt-5">
                      <Label htmlFor="typed-sig">Type your full legal name as your signature</Label>
                      <Input id="typed-sig" className="mt-1.5" value={typedSig} onChange={(e) => setTypedSig(e.target.value)} placeholder={voter.full_name || "Your full name"} data-testid="wizard-typed-signature-input" />
                      {typedSig.trim().length >= 2 && (
                        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Signature preview</p>
                          <p className="text-2xl text-foreground" style={{ fontFamily: "'Space Grotesk', cursive", fontStyle: "italic" }}>{typedSig}</p>
                        </div>
                      )}
                    </TabsContent>
                    <TabsContent value="drawn" className="mt-5">
                      <SignatureCanvas onChange={setDrawnSig} />
                      <p className="text-xs text-muted-foreground mt-2">
                        Signature images only. Never upload or attach a ballot photograph — it will be quarantined and reviewed.
                      </p>
                    </TabsContent>
                    <TabsContent value="paper" className="mt-5">
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        Prefer to sign on paper? Print the blank declaration form, complete it by hand, and keep it with your
                        records. Your electronic declaration will be recorded now with a “paper signature to follow” marker.
                      </p>
                      <Button variant="outline" onClick={printPaperForm} data-testid="wizard-print-paper-form">
                        <Printer className="h-4 w-4 mr-2" /> Print paper declaration form
                      </Button>
                      <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3.5 mt-4 cursor-pointer">
                        <Checkbox checked={paperConfirm} onCheckedChange={(v) => setPaperConfirm(!!v)} className="mt-0.5" data-testid="wizard-paper-confirm" />
                        <span className="text-sm text-foreground leading-relaxed">
                          I will complete and retain the printed paper declaration form. Record my declaration now with my paper signature to follow.
                        </span>
                      </label>
                    </TabsContent>
                  </Tabs>

                  <Separator className="my-6" />
                  <h2 className="font-heading font-semibold text-sm text-foreground mb-3">Required consents</h2>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3.5 cursor-pointer">
                      <Checkbox checked={consents.verification} onCheckedChange={(v) => setConsents((c) => ({ ...c, verification: !!v }))} className="mt-0.5" data-testid="wizard-consent-verification" />
                      <span className="text-sm text-foreground leading-relaxed">
                        I consent to being contacted to verify this declaration.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3.5 cursor-pointer">
                      <Checkbox checked={consents.evidence_retention} onCheckedChange={(v) => setConsents((c) => ({ ...c, evidence_retention: !!v }))} className="mt-0.5" data-testid="wizard-consent-retention" />
                      <span className="text-sm text-foreground leading-relaxed">
                        I consent to my declaration being securely retained as evidence for independent verification.
                      </span>
                    </label>
                  </div>

                  <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={() => setStep(3)} data-testid="wizard-back-button">
                      <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="wizard-submit-button">
                      {submitting ? "Submitting…" : "Submit my declaration"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 5: receipt */}
            {step === 5 && receipt && (
              <div className="space-y-5">
                <Card className="receipt-card border-primary/30">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-11 w-11 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="h-6 w-6 text-emerald-700" />
                      </div>
                      <div>
                        <h1 className="font-heading font-bold text-xl text-foreground">Declaration received</h1>
                        <p className="text-xs text-muted-foreground">Securely preserved with a cryptographic integrity hash.</p>
                      </div>
                      {receipt.is_test && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 ml-auto" data-testid="receipt-test-badge">TEST</Badge>
                      )}
                    </div>

                    {receipt.duplicate_notice && (
                      <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-xs px-3.5 py-2.5 mb-5" data-testid="receipt-duplicate-notice">
                        A similar declaration already exists for this candidate. Our review team will reconcile duplicates — no action is needed from you.
                      </div>
                    )}

                    <div className="rounded-xl border border-slate-300 bg-white p-5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Declaration number</p>
                      <p className="font-mono text-2xl font-semibold text-foreground" data-testid="receipt-declaration-number">{receipt.declaration_number}</p>
                      <Separator className="my-4" />
                      <div className="grid sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Declarant</p>
                          <p className="font-medium text-foreground">{receipt.voter_full_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Candidate</p>
                          <p className="font-medium text-foreground">{receipt.candidate_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Office</p>
                          <p className="font-medium text-foreground">{receipt.office_label}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Received (UTC)</p>
                          <p className="font-medium text-foreground font-mono text-xs mt-0.5">{receipt.submitted_at}</p>
                        </div>
                      </div>
                      <Separator className="my-4" />
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">SHA-256 integrity hash</p>
                      <p className="font-mono text-[11px] text-slate-700 break-all leading-relaxed" data-testid="receipt-integrity-hash">{receipt.integrity_hash}</p>
                      {receipt.verify_url && (
                        <>
                          <Separator className="my-4" />
                          <div className="flex items-start gap-4" data-testid="receipt-qr-block">
                            <img
                              src={receipt.qr_url}
                              alt="QR code — scan to verify this receipt"
                              className="h-28 w-28 rounded-md border border-slate-300 bg-white"
                              data-testid="receipt-qr-image"
                            />
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Verify this receipt</p>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                Scan the QR code or visit{" "}
                                <Link to={`/verify/${receipt.declaration_number}`} className="text-primary font-medium hover:underline font-mono text-[11px]" data-testid="receipt-verify-link">
                                  /verify/{receipt.declaration_number}
                                </Link>
                                . Verification shows record status only — it never reveals your identity, address or signature.
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2.5 mt-5">
                      <Button variant="outline" onClick={printReceipt} data-testid="receipt-print-button">
                        <Printer className="h-4 w-4 mr-2" /> Print
                      </Button>
                      <Button variant="outline" onClick={downloadReceipt} data-testid="receipt-download-button">
                        <Download className="h-4 w-4 mr-2" /> Download receipt
                      </Button>
                      <CopyButton text={receipt.integrity_hash} label="Copy hash" testId="receipt-copy-hash" />
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed mt-5">
                      Keep this receipt safe. A copy has also been prepared for our records. You can confirm your record
                      remains preserved and unaltered anytime on the{" "}
                      <Link to="/verify" className="text-primary font-medium hover:underline">Verify a Declaration</Link> page.
                      Your declaration is private — it is never shown to candidates or the public, and no totals are released
                      while voting is open.
                    </p>
                  </CardContent>
                </Card>

                {/* NEXT STEP: GET WITNESSED (Mutual Witness system) */}
                {receipt.owner_token && (
                  <Card className="border-accent/40 bg-emerald-50/40" data-testid="receipt-witness-next-card">
                    <CardContent className="p-6 sm:p-8">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-accent mb-1">Next step</p>
                      <h2 className="font-heading font-bold text-xl text-foreground">Don't just declare it. Witness it.</h2>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                        Ask someone you trust to witness your declaration. They confirm that <span className="font-semibold text-foreground">you</span> made
                        and signed it — they never see your ballot or how you voted.
                      </p>
                      {receipt.witness_code && (
                        <div className="rounded-lg bg-white border border-slate-300 p-4 mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Witness code</p>
                            <p className="font-mono font-bold text-2xl text-foreground" data-testid="receipt-witness-code">{receipt.witness_code}</p>
                          </div>
                          <p className="text-xs text-muted-foreground flex-1 min-w-[180px]">
                            Your witness can enter this code at <span className="font-mono">/witness</span> — or use your private link and QR code on your declaration page.
                          </p>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2.5 mt-5">
                        <Button asChild className="bg-accent hover:bg-accent/90" data-testid="receipt-open-my-declaration">
                          <Link to={`/my-declaration/${receipt.owner_token}`}>Get Witnessed — Open My Declaration Page</Link>
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-3">
                        Bookmark your declaration page — it holds your private witness link, QR code and receipt.
                      </p>
                    </CardContent>
                  </Card>
                )}
                <div className="flex justify-between">
                  <Button asChild variant="outline"><Link to={candidate.path}>Back to candidate page</Link></Button>
                  <Button asChild variant="ghost"><Link to="/">Home</Link></Button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
